export const timingRecommendationStates = [
  "eligible-for-editorial-review",
  "accepted-to-draft",
  "rejected",
  "deferred",
  "review-due",
  "stale",
  "withdrawn",
] as const;
export type TimingRecommendationState = (typeof timingRecommendationStates)[number];

export const timingRecommendationAcceptReasons = [
  "protected-pattern-is-useful",
  "protected-and-public-evidence-align",
  "other-explanation-required",
] as const;
export type TimingRecommendationAcceptReason = (typeof timingRecommendationAcceptReasons)[number];

export const timingRecommendationRejectReasons = [
  "not-sufficiently-useful",
  "safety-or-sensitivity-concern",
  "evidence-remains-confounded",
  "duplicate-recommendation",
  "public-evidence-does-not-support-change",
  "outside-supported-product-contract",
  "other-explanation-required",
] as const;
export type TimingRecommendationRejectReason = (typeof timingRecommendationRejectReasons)[number];

export const timingRecommendationDeferReasons = [
  "more-evidence-required",
  "public-source-review",
  "defect-resolved",
  "market-rule-changed",
] as const;
export type TimingRecommendationDeferReason = (typeof timingRecommendationDeferReasons)[number];

export type TimingRecommendationDecisionInput =
  | Readonly<{
      disposition: "accept";
      reason: TimingRecommendationAcceptReason;
      rationale: string;
      trigger: null;
    }>
  | Readonly<{
      disposition: "reject";
      reason: TimingRecommendationRejectReason;
      rationale: string | null;
      trigger: null;
    }>
  | Readonly<{
      disposition: "defer";
      reason: TimingRecommendationDeferReason;
      rationale: string | null;
      trigger:
        | Readonly<{ kind: "review-date"; reviewAtEpochMilliseconds: number }>
        | Readonly<{
            kind: "evidence-condition";
            condition: TimingRecommendationDeferReason;
            reference: string;
          }>;
    }>;

export type TimingRecommendationTrigger = Extract<
  TimingRecommendationDecisionInput,
  { disposition: "defer" }
>["trigger"];

export type NormalizedTimingRecommendationDecision =
  | (Extract<TimingRecommendationDecisionInput, { disposition: "accept" }> &
      Readonly<{ resultingState: "accepted-to-draft" }>)
  | (Extract<TimingRecommendationDecisionInput, { disposition: "reject" }> &
      Readonly<{ resultingState: "rejected" }>)
  | (Extract<TimingRecommendationDecisionInput, { disposition: "defer" }> &
      Readonly<{ resultingState: "deferred" }>);

function validBoundedText(value: string, maximum: number): boolean {
  return (
    value.trim() === value &&
    value.length <= maximum &&
    !/[<>]/u.test(value) &&
    ![...value].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
  );
}

function normalizedRationale(value: string | null, required: boolean): string | null {
  if (value === null) {
    if (required) throw new Error("Add a brief editorial rationale.");
    return null;
  }
  if (!validBoundedText(value, 500) || (required && value.length === 0))
    throw new Error("Use a plain-text rationale of no more than 500 characters.");
  return value.length === 0 ? null : value;
}

function addUtcCalendarMonths(epochMilliseconds: number, months: number): number {
  if (!Number.isSafeInteger(epochMilliseconds) || epochMilliseconds < 0)
    throw new Error("Use a valid decision time.");
  const source = new Date(epochMilliseconds);
  const first = Date.UTC(
    source.getUTCFullYear(),
    source.getUTCMonth() + months,
    1,
    source.getUTCHours(),
    source.getUTCMinutes(),
    source.getUTCSeconds(),
    source.getUTCMilliseconds(),
  );
  const lastDay = new Date(
    Date.UTC(new Date(first).getUTCFullYear(), new Date(first).getUTCMonth() + 1, 0),
  ).getUTCDate();
  const target = new Date(first);
  target.setUTCDate(Math.min(source.getUTCDate(), lastDay));
  return target.getTime();
}

export function timingRecommendationDecisionExpiry(decidedAtEpochMilliseconds: number): number {
  return addUtcCalendarMonths(decidedAtEpochMilliseconds, 24);
}

/**
 * Validates the closed Question-20C decision vocabulary. This does not authorize the decision or
 * create a draft; the transaction boundary must revalidate both before persisting anything.
 */
export function normalizeTimingRecommendationDecision(
  input: TimingRecommendationDecisionInput,
  decidedAtEpochMilliseconds: number,
): NormalizedTimingRecommendationDecision {
  if (input.disposition === "accept") {
    if (!timingRecommendationAcceptReasons.includes(input.reason))
      throw new Error("Use an approved acceptance reason.");
    return {
      ...input,
      rationale: normalizedRationale(input.rationale, true)!,
      resultingState: "accepted-to-draft",
    };
  }
  if (input.disposition === "reject") {
    if (!timingRecommendationRejectReasons.includes(input.reason))
      throw new Error("Use an approved rejection reason.");
    return {
      ...input,
      rationale: normalizedRationale(
        input.rationale,
        input.reason === "other-explanation-required",
      ),
      resultingState: "rejected",
    };
  }
  if (!timingRecommendationDeferReasons.includes(input.reason))
    throw new Error("Use an approved deferral reason.");
  const trigger = input.trigger;
  if (trigger.kind === "review-date") {
    const latest = addUtcCalendarMonths(decidedAtEpochMilliseconds, 12);
    if (
      !Number.isSafeInteger(trigger.reviewAtEpochMilliseconds) ||
      trigger.reviewAtEpochMilliseconds <= decidedAtEpochMilliseconds ||
      trigger.reviewAtEpochMilliseconds > latest
    )
      throw new Error("Choose a future review date within 12 calendar months.");
  } else {
    if (trigger.condition !== input.reason)
      throw new Error("The evidence condition must match the closed deferral reason.");
    if (!validBoundedText(trigger.reference, 160) || trigger.reference.length < 1)
      throw new Error("Add a bounded evidence-condition reference.");
  }
  return {
    ...input,
    rationale: normalizedRationale(input.rationale, false),
    resultingState: "deferred",
  };
}

export function timingRecommendationReviewState(
  state: TimingRecommendationState,
  trigger: TimingRecommendationTrigger | null,
  nowEpochMilliseconds: number,
): TimingRecommendationState {
  if (
    state === "deferred" &&
    trigger?.kind === "review-date" &&
    nowEpochMilliseconds >= trigger.reviewAtEpochMilliseconds
  )
    return "review-due";
  return state;
}
